import { useState } from "react";
import { motion } from "framer-motion";
import { Mail, Globe, Building2, Send, CheckCircle, AlertCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";

export function ContactSection() {
  const [form, setForm] = useState({ name: "", email: "", type: "", message: "" });
  const [status, setStatus] = useState<"idle" | "sending" | "ok" | "error">("idle");
  const [errMsg, setErrMsg] = useState("");

  const set = (k: string, v: string) => setForm(p => ({ ...p, [k]: v }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return;
    setStatus("sending");
    try {
      const res = await fetch("/contact.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (data.success) {
        setStatus("ok");
        setForm({ name: "", email: "", type: "", message: "" });
      } else {
        setStatus("error");
        setErrMsg(data.error || "Something went wrong.");
      }
    } catch {
      setStatus("error");
      setErrMsg("Connection error. Please email us at efisfly@efisfly.com");
    }
  };

  return (
    <section id="contact" className="py-24">
      <div className="container mx-auto px-6">
        <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          transition={{ type: "spring", stiffness: 280, damping: 32 }} className="mb-12">
          <p className="text-xs font-mono tracking-[0.25em] uppercase font-bold mb-2" style={{ color: "oklch(0.55 0.22 25)" }}>— Contact —</p>
          <h2 className="text-4xl md:text-5xl font-black text-foreground" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            Get in <span style={{ color: "oklch(0.28 0.09 240)" }}>Touch</span>
          </h2>
          <p className="text-muted-foreground mt-2 max-w-lg">
            Ready to upgrade your aviation training setup? Contact us for licensing, custom development, or B737 MAX information.
          </p>
        </motion.div>

        <div className="max-w-4xl grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Left info */}
          <motion.div initial={{ opacity: 0, x: -24 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
            transition={{ type: "spring", stiffness: 280, damping: 32 }} className="space-y-4">
            {[
              { icon: Globe, label: "Website", value: "www.efisfly.com", href: "https://www.efisfly.com", color: "oklch(0.28 0.09 240)" },
              { icon: Mail, label: "Email", value: "efisfly@efisfly.com", href: "mailto:efisfly@efisfly.com", color: "oklch(0.28 0.09 240)" },
            ].map(c => {
              const Icon = c.icon;
              return (
                <div key={c.label} className="bg-white rounded-xl border border-border p-5 shadow-[0_2px_10px_oklch(0.12_0.03_240/0.05)]"
                  style={{ borderLeft: `4px solid ${c.color}` }}>
                  <div className="flex items-center gap-3 mb-2">
                    <Icon className="w-4 h-4 text-primary" />
                    <span className="font-bold text-sm text-foreground">{c.label}</span>
                  </div>
                  <a href={c.href} className="text-sm font-mono text-primary hover:underline">{c.value}</a>
                </div>
              );
            })}
            <div className="bg-white rounded-xl border border-border p-5 shadow-[0_2px_10px_oklch(0.12_0.03_240/0.05)]"
              style={{ borderLeft: "4px solid oklch(0.55 0.22 25)" }}>
              <div className="flex items-center gap-3 mb-2">
                <Building2 className="w-4 h-4" style={{ color: "oklch(0.55 0.22 25)" }} />
                <span className="font-bold text-sm text-foreground">Licensing Enquiries</span>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed">
                For ATO / airline licensing, custom development, B737 MAX or enterprise enquiries — use the form or email us directly.
              </p>
            </div>
          </motion.div>

          {/* Form */}
          <motion.div initial={{ opacity: 0, x: 24 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
            transition={{ type: "spring", stiffness: 280, damping: 32 }}
            className="bg-white rounded-xl border border-border p-7 shadow-[0_4px_20px_oklch(0.12_0.03_240/0.08)]">

            {status === "ok" ? (
              <div className="flex flex-col items-center justify-center h-full py-12 text-center gap-4">
                <CheckCircle className="w-14 h-14 text-emerald-500" />
                <h3 className="text-xl font-bold text-foreground">Message Sent!</h3>
                <p className="text-muted-foreground text-sm max-w-xs">
                  Thank you. We'll get back to you shortly. A confirmation has been sent to your email.
                </p>
                <Button variant="outline" className="mt-2" onClick={() => setStatus("idle")}>
                  Send Another
                </Button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4">
                {[
                  { key: "name", label: "Name / Organization", type: "text", placeholder: "Your name or ATO name" },
                  { key: "email", label: "Email", type: "email", placeholder: "your@email.com" },
                ].map(f => (
                  <div key={f.key}>
                    <label className="block text-xs font-bold text-muted-foreground tracking-wider uppercase mb-1.5">{f.label}</label>
                    <input type={f.type} placeholder={f.placeholder} value={form[f.key as keyof typeof form]}
                      onChange={e => set(f.key, e.target.value)} required
                      className="w-full bg-secondary border border-border rounded-lg px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/20 transition-all" />
                  </div>
                ))}
                <div>
                  <label className="block text-xs font-bold text-muted-foreground tracking-wider uppercase mb-1.5">Enquiry Type</label>
                  <select value={form.type} onChange={e => set("type", e.target.value)}
                    className="w-full bg-secondary border border-border rounded-lg px-4 py-2.5 text-sm text-foreground focus:outline-none focus:border-primary transition-all">
                    <option value="">Select type...</option>
                    <option>Professional License (G1000NXi)</option>
                    <option>B737 MAX — Request Information</option>
                    <option>B737 MAX — Demo Presentation</option>
                    <option>Custom Avionics Development</option>
                    <option>Airport Development</option>
                    <option>CBT Software</option>
                    <option>Enterprise / Custom Project</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold text-muted-foreground tracking-wider uppercase mb-1.5">Message</label>
                  <textarea rows={4} placeholder="Tell us about your training requirements..."
                    value={form.message} onChange={e => set("message", e.target.value)} required
                    className="w-full bg-secondary border border-border rounded-lg px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/20 transition-all resize-none" />
                </div>

                {status === "error" && (
                  <div className="flex items-start gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
                    <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>{errMsg}</span>
                  </div>
                )}

                <Button type="submit" disabled={status === "sending"}
                  className="w-full bg-primary text-primary-foreground font-bold shadow-[0_4px_14px_oklch(0.28_0.09_240/0.35)] hover:shadow-[0_6px_20px_oklch(0.28_0.09_240/0.45)] transition-shadow disabled:opacity-60">
                  {status === "sending"
                    ? <><Loader2 className="mr-2 w-4 h-4 animate-spin" /> Sending...</>
                    : <><Send className="mr-2 w-4 h-4" /> Send Enquiry</>}
                </Button>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
